import { apiFetch } from "api/utils/apiFetch";
import { createApiError, getAuthHeaders } from "api/utils/servicesUtils";
import { resolveBrandingAssetUrl } from "utils/branding/brandingResolver";

export const PRE_LOGIN_BRANDING_ENDPOINT = "/api/auth/ui-config/login/";
export const POST_LOGIN_BRANDING_ENDPOINT = "/api/auth/ui-config/app/";

const PUBLIC_JSON_HEADERS = Object.freeze({
  "Content-Type": "application/json",
});

const LOGIN_LOGO_FIELDS = Object.freeze(["login_logo_url"]);
const APP_LOGO_FIELDS = Object.freeze(["header_logo_url", "sidebar_logo_url"]);

export const isBrandingOfflineMode = () => process.env.REACT_APP_MODE_OFFLINE === "true";

async function readBrandingConfig(endpoint, headers, errorMessage, signal, meta) {
  const response = await apiFetch(endpoint, {
    method: "GET",
    headers,
    ...(signal && { signal }),
  }, meta);

  if (!response.ok) {
    throw await createApiError(response, errorMessage);
  }

  return response.json();
}

async function probeLogoUrl(url, signal) {
  const response = await apiFetch(url, {
    method: "GET",
    ...(signal && { signal }),
  }, { skipAuthHandling: true });

  return {
    ok: response.ok,
    status: response.status,
  };
}

/**
 * Probes each logo URL in a branding payload. Unreachable / non-ok logo
 * endpoints are nulled so the resolver falls through to env / product defaults.
 * Colors and text fields are left untouched. Transient network errors keep the
 * original URL so the browser `<img>` can still attempt the load.
 *
 * @param {object|null} payload
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {string[]} [options.logoFields]
 * @returns {Promise<object|null>}
 */
export async function sanitizeLogoUrls(payload, { signal, logoFields } = {}) {
  if (!payload || typeof payload !== "object") return payload;

  const fields = logoFields ?? Object.keys(payload).filter((key) => key.endsWith("_logo_url"));
  if (fields.length === 0) return payload;

  const next = { ...payload };

  await Promise.all(fields.map(async (field) => {
    if (signal?.aborted) {
      const error = new Error("Aborted");
      error.name = "AbortError";
      throw error;
    }

    const raw = next[field];
    if (typeof raw !== "string" || !raw.trim()) {
      next[field] = null;
      return;
    }

    const assetUrl = resolveBrandingAssetUrl(raw);
    if (!assetUrl) {
      next[field] = null;
      return;
    }

    try {
      const { ok, status } = await probeLogoUrl(assetUrl, signal);
      if (!ok) {
        console.error(
          `Branding logo probe failed for ${field}: ${assetUrl} returned ${status}. Falling back to env/default.`,
        );
        next[field] = null;
      }
    } catch (error) {
      if (error?.name === "AbortError" || signal?.aborted) throw error;
      console.error(
        `Branding logo probe failed for ${field}: ${assetUrl}. Keeping URL for browser <img> retry.`,
        error,
      );
      // Keep the original URL on transient transport failures.
    }
  }));

  return next;
}

/**
 * Fetches the public login UI config using the backend response contract as-is:
 * { scope, background_color, login_logo_url }.
 * Broken login_logo_url values are nulled before return.
 */
export async function fetchPreLoginBranding({ signal } = {}) {
  if (isBrandingOfflineMode()) return null;

  const payload = await readBrandingConfig(
    PRE_LOGIN_BRANDING_ENDPOINT,
    PUBLIC_JSON_HEADERS,
    "Unable to fetch login branding.",
    signal,
    { skipAuthHandling: true }
  );

  return sanitizeLogoUrls(payload, { signal, logoFields: LOGIN_LOGO_FIELDS });
}

/**
 * Fetches the authenticated app UI config using the backend response contract
 * as-is: { scope, header_text, header_background_color,
 * sidebar_background_color, header_logo_url, sidebar_logo_url }.
 * Broken logo URL fields are nulled before return.
 */
export async function fetchPostLoginBranding({ signal } = {}) {
  if (isBrandingOfflineMode()) return null;

  const payload = await readBrandingConfig(
    POST_LOGIN_BRANDING_ENDPOINT,
    getAuthHeaders(),
    "Unable to fetch app branding.",
    signal
  );

  return sanitizeLogoUrls(payload, { signal, logoFields: APP_LOGO_FIELDS });
}
